import {
  I2cInput,
  I2cListItem,
  I2cRadio,
  I2cRadioGroup,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";

const cooperativeAuthenticationOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

const maintainBalanceOptions = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
];

// Validation schema
const validationSchema = yup.object().shape({
  cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c:
    yup.string().optional(),
  cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records:
    yup
      .string()
      .when(
        "cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c",
        {
          is: "yes",
          then: (schema) => schema.optional(),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
  cooperative_authentication_who_is_providing_core_banking_services: yup
    .string()
    .when(
      "cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records",
      {
        is: "yes",
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.notRequired(),
      },
    ),
});

const CooperativeAuthentication = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c:
        "",
      cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records:
        "",
      cooperative_authentication_who_is_providing_core_banking_services: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c",
    "cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records",
    "cooperative_authentication_who_is_providing_core_banking_services",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData((prev) => ({
      ...prev,
      7: {
        ...prev[7],
        "7_0": { ...values },
      },
    }));
  }, [methods, setAddFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[7]?.["7_0"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("7_0", true);
    } else {
      setFormValidStates("7_0", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleRadioChange = (fieldName, value, fieldsToReset = {}) => {
    methods.setValue(fieldName, value, { shouldValidate: true });

    // Clear dependent fields if needed
    Object.keys(fieldsToReset).forEach((key) => {
      methods.setValue(key, fieldsToReset[key]);
    });

    handleValueChanges();
  };

  const handleInputChange = (fieldName, value) => {
    methods.setValue(fieldName, value, { shouldValidate: true });
    handleValueChanges();
  };

  // Handler for cooperative authentication radio
  const handleCooperativeAuthenticationChange = (e) => {
    const value = e.target?.value || e;
    handleRadioChange(
      "cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c",
      value,
      value !== "yes"
        ? {
            cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records:
              "",
            cooperative_authentication_who_is_providing_core_banking_services:
              "",
          }
        : {},
    );
  };

  // Handler for maintain balance radio
  const handleMaintainBalanceChange = (e) => {
    const value = e.target?.value || e;
    handleRadioChange(
      "cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records",
      value,
      value !== "yes"
        ? {
            cooperative_authentication_who_is_providing_core_banking_services:
              "",
          }
        : {},
    );
  };

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
          <div className="auth-heading mb-16">Opting for Cooperative Authentication</div>
            <div className="sub-section">
              <I2cRadioGroup
                label="Would you like to opt for Cooperative (mutual) Authorization with i2c?"
                value={methods.watch(
                  "cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c",
                )}
                onI2cChange={handleCooperativeAuthenticationChange}
              >
                <div i2c-grid="grid gap-sm">
                  {cooperativeAuthenticationOptions.map((radio, index) => (
                    <I2cListItem
                      key={index}
                      i2c-grid="4@md"
                      variant="receipt"
                      label={radio.label}
                      sublabel=" "
                      type="checkbox"
                    >
                      <I2cRadio slot="prefix" filled value={radio.value} />
                    </I2cListItem>
                  ))}
                </div>
              </I2cRadioGroup>
              {errors.cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c && (
                <ErrorMessage
                  message={
                    errors
                      .cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c
                      .message
                  }
                />
              )}
            </div>
            {methods.watch(
              "cooperative_authentication_would_you_like_opt_for_cooperative_authorization_with_i2c",
            ) === "yes" && (
              <>
                <div className="sub-section">
                  <I2cRadioGroup
                    label="Please confirm if client would like to maintain the balances and will be the system of record?"
                    value={methods.watch(
                      "cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records",
                    )}
                    onI2cChange={handleMaintainBalanceChange}
                  >
                    <div i2c-grid="grid gap-sm">
                      {maintainBalanceOptions.map((radio, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={radio.label}
                          sublabel=" "
                          type="checkbox"
                        >
                          <I2cRadio slot="prefix" filled value={radio.value} />
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cRadioGroup>
                  {errors.cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records && (
                    <ErrorMessage
                      message={
                        errors
                          .cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records
                          .message
                      }
                    />
                  )}
                </div>
                {methods.watch(
                  "cooperative_authentication_please_confirm_if_client_would_like_to_maintain_the_balances_and_will_be_the_system_of_records",
                ) === "yes" && (
                  <div i2c-grid="grid gap-sm" className="mt-16 sub-section">
                    <div i2c-grid="4@md">
                      <div className="single-field-wrapper">
                        <I2cInput
                          className="single-input-field"
                          onI2cInput={(e) =>
                            handleInputChange(
                              "cooperative_authentication_who_is_providing_core_banking_services",
                              e.target?.value || e.detail?.value || "",
                            )
                          }
                          value={
                            methods.watch(
                              "cooperative_authentication_who_is_providing_core_banking_services",
                            ) || ""
                          }
                          placeholder="Please Specify"
                          label="Who is providing Core Banking services?"
                          name="cooperative_authentication_who_is_providing_core_banking_services"
                          size="large"
                        />
                        {errors.cooperative_authentication_who_is_providing_core_banking_services && (
                          <ErrorMessage
                            message={
                              errors
                                .cooperative_authentication_who_is_providing_core_banking_services
                                .message
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CooperativeAuthentication;
